import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { LinkColor, LinkHoverColor } from '@freelancer/ui/link';
import {
  FontColor,
  FontType,
  FontWeight,
  HighlightColor,
  ReadMore,
  ReadMoreColor,
  TextSize,
} from '@freelancer/ui/text';
import { emojiIdToNative, legacyEmojiRegex } from './interactive-text.types';

/**
 * A component for converting links and/or emojis within a text block into actual links/emojis.
 * Can take in (some) inputs that fl-text or fl-link can take in.
 */
@Component({
  selector: 'fl-interactive-text',
  template: `
    <ng-container *ngIf="link; else plainText">
      <fl-text
        *flAutoLink="
          true;
          color: linkColor;
          highlightColor: highlightColor;
          hoverColor: LinkHoverColor.INHERIT
        "
        [color]="fontColor"
        [displayLineBreaks]="displayLineBreaks"
        [highlightColor]="highlightColor"
        [fontType]="FontType.SPAN"
        [readMore]="readMore"
        [readMoreColor]="readMoreColor"
        [size]="fontSize"
        [weight]="fontWeight"
        [maxLines]="maxLines"
      >
        {{ text }}
      </fl-text>
    </ng-container>

    <ng-template #plainText>
      <fl-text
        [color]="fontColor"
        [displayLineBreaks]="displayLineBreaks"
        [highlightColor]="highlightColor"
        [fontType]="FontType.SPAN"
        [readMore]="readMore"
        [readMoreColor]="readMoreColor"
        [size]="fontSize"
        [weight]="fontWeight"
        [maxLines]="maxLines"
      >
        {{ text }}
      </fl-text>
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InteractiveTextComponent implements OnChanges {
  FontType = FontType;
  LinkHoverColor = LinkHoverColor;

  @Input() content: string;
  @Input() highlightColor?: HighlightColor;
  @Input() linkColor: LinkColor = LinkColor.DEFAULT;
  @Input() fontColor: FontColor = FontColor.FOREGROUND;
  @Input() fontSize: TextSize = TextSize.XXSMALL;
  @Input() fontWeight: FontWeight = FontWeight.NORMAL;
  @Input() link = false;
  @Input() emoji = false;
  @Input() displayLineBreaks = true;
  @Input() maxLines?: number;
  @Input() readMore = ReadMore.NONE;
  @Input() readMoreColor?: ReadMoreColor;

  text: string;

  ngOnChanges(changes: SimpleChanges): void {
    if ('content' in changes) {
      this.text = this.replaceEmojis(this.content);
    }
  }

  replaceEmojis(text: string): string {
    if (!this.emoji) {
      return text;
    }

    const urlRegex = /https?:\/\/[^\s/$.?#].[^\s]*/g;

    // Split the text into parts based on URL matches
    const parts = text.split(urlRegex);
    const urls = text.match(urlRegex);

    // Replace emojis in each part that is not a URL
    for (let i = 0; i < parts.length; i++) {
      parts[i] = parts[i].replace(
        new RegExp(legacyEmojiRegex, 'g'),
        match => emojiIdToNative[match],
      );
    }

    // Reconstruct the text by interleaving parts and URLs
    let result = '';
    for (let i = 0; i < parts.length; i++) {
      result += parts[i];
      if (urls && urls[i]) {
        result += urls[i];
      }
    }

    return result;
  }
}
